import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Grid, Pagination } from '@mui/material';
import { backend } from '../../statics/system_variables';
import { AnimalCard, SkeletonCard } from './card';
import { Helmet } from 'react-helmet-async';

export default function AnimalContent(props) {

    //The xs, sm, md, lg, and xl props
    const xs = 12;
    const sm = 6;
    const md = 6;
    const lg = 4;
    const xl = 4;

    const [dogs, setDogs] = useState([[], 0, 0]);
    const [loading, setLoading] = useState(true);
    const [apiQuery, setApiQuery] = useState('');

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const path = location.pathname;

    const { setDogs: setParentDogs, breed_id } = props;

    // Memoize the current page from URL to use as a stable value in effects unless searchParams changes
    const currentPageFromUrl = useMemo(() => {
        const pageParam = searchParams.get('page');
        const parsedPage = parseInt(pageParam, 10);
        return !isNaN(parsedPage) && parsedPage > 0 ? parsedPage : 1;
    }, [searchParams]);

    // Store the previous "filter signature" to detect changes that require a page reset
    const prevFilterSignatureRef = useRef('');

    useEffect(() => {
        // Create a "filter signature" from path, breed_id, and searchParams (excluding 'page')
        const currentParamsSignature = new URLSearchParams(searchParams);
        currentParamsSignature.delete('page');
        const currentFilterSignature = `${path}-${breed_id || ''}-${currentParamsSignature.toString()}`;

        let targetPage = currentPageFromUrl;

        // Check if filters changed compared to the last run
        if (prevFilterSignatureRef.current && prevFilterSignatureRef.current !== currentFilterSignature) {
            // Filters have changed, so we should go to page 1
            targetPage = 1;
        }

        if (targetPage !== currentPageFromUrl) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('page', targetPage.toString());
            setSearchParams(newSearchParams, { replace: true });
            return;
        }

        prevFilterSignatureRef.current = currentFilterSignature;

        // Proceed to build and set the API query
        setLoading(true);
        const queryForAPI = new URLSearchParams(searchParams);
        queryForAPI.set('page', targetPage.toString());

        // Add path-based params (state, breed_id)
        if (path.includes('/Bundesland/')) {
            const stateMatch = decodeURI(path).match(/Bundesland\/([\wäüöß-]+)/);
            if (stateMatch && stateMatch[1]) {
                queryForAPI.set('state', encodeURIComponent(stateMatch[1]));
            }
        } else if (path.includes('/Rasse/') && breed_id) {
            queryForAPI.set('breed_id', encodeURIComponent(breed_id));
        }

        const finalAPIQuery = `${backend}/dogs?${queryForAPI.toString()}`;
        setApiQuery(finalAPIQuery);

    }, [searchParams, path, breed_id, backend, setSearchParams, currentPageFromUrl]);

    // API fetching effect
    useEffect(() => {
        if (apiQuery) {
            axios.get(apiQuery)
                .then(({ data }) => {
                    setDogs(data);
                    if (setParentDogs) {
                        setParentDogs(data);
                    }
                })
                .catch((error) => {
                    console.error("Failed to fetch dogs:", error);
                    setDogs([[], 0, 0]);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setDogs([[], 0, 0]);
            setLoading(false);
        }
        window.scrollTo(0, 0);
    }, [apiQuery, setParentDogs]);

    const handlePageChange = (event, newPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', newPage.toString());
        setSearchParams(newSearchParams, { replace: true });
    };

    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(12)].map((currel, index) => (
                    <React.Fragment key={index.toString()}>
                        <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                            <SkeletonCard index={index} />
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        );
    }

    return <Grid container spacing={2}>

        {dogs[2] > 0 ? <></> : <Helmet><meta name="robots" content="noindex" /></Helmet>}

        {dogs[0].map((dog) => (
            <React.Fragment key={dog.dog_link}>
                <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
                    <AnimalCard dog={dog} />
                </Grid>
            </React.Fragment>
        ))}

        <Grid item xs={12}>
            <Pagination count={dogs[1]} page={currentPageFromUrl} variant="outlined" shape="rounded" boundaryCount={2} sx={{ justifyContent: 'center', display: 'flex' }} onChange={handlePageChange} />
        </Grid>
    </Grid>;
}
